import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../services/api';
import { AuthContext } from './AuthContext';

// ---------------------------------------------------------------------------
// The credit balance, in one place.
//
// It is shown in the header on every screen, on the dashboard, and beside
// every action that spends it. Fetching it separately on each of those would
// mean four screens quoting four numbers the moment one of them charged —
// so it is fetched once here and every consumer reads the same value.
//
// `refresh()` is what keeps it honest. Anything that spends credits calls it
// when the spend returns, and the header updates without a page reload. A
// balance that only corrects itself on navigation is a balance a seller
// cannot trust.
// ---------------------------------------------------------------------------

export const CreditsContext = createContext({
  balance: null,
  plan: null,
  exempt: false,
  reason: null,
  history: [],
  loading: true,
  refresh: () => {}
});

export function CreditsProvider({ children }) {
  const { isAuthenticated, user } = useContext(AuthContext);
  const [state, setState] = useState({ balance: null, plan: null, exempt: false, reason: null, history: [], loading: true });

  const refresh = useCallback(async () => {
    // Vendors have no credits — they supply products, they do not list them.
    // Asking on their behalf would be a guaranteed 404 on every page load.
    if (!isAuthenticated || user?.role === 'vendor') {
      setState({ balance: null, plan: null, exempt: false, reason: null, history: [], loading: false });
      return;
    }
    try {
      const res = await api.get('/credits');
      setState({
        balance: res.data.balance,
        plan: res.data.plan,
        exempt: res.data.exempt === true,
        reason: res.data.reason || null,
        history: res.data.history || [],
        loading: false
      });
    } catch {
      // A failed balance read must not break the page it sits on. The pill
      // simply does not render until the next successful refresh.
      setState((s) => ({ ...s, loading: false }));
    }
  }, [isAuthenticated, user?.role]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <CreditsContext.Provider value={{ ...state, refresh }}>
      {children}
    </CreditsContext.Provider>
  );
}

/** Convenience for the many places that only need to say "spent — recount". */
export const useCredits = () => useContext(CreditsContext);
